/**
 * StatsCard - Premium 2026 KPI/Statistics Card Component
 * 
 * Ultra-modern KPI display with:
 * - Glassmorphism effects
 * - Animated gradient borders
 * - Glow effects on hover
 * - Micro-animations
 * - Premium typography
 * 
 * @component
 */
import React from 'react';
import { motion as Motion } from 'framer-motion';
import {
    Card,
    CardContent,
    Box,
    Typography,
    Chip,
    Skeleton,
    Tooltip,
    IconButton,
    Avatar
} from '@mui/material';
import {
    TrendingUp as TrendUpIcon,
    TrendingDown as TrendDownIcon,
    ArrowForward as ArrowIcon,
    NorthEast as GrowthIcon
} from '@mui/icons-material';

/**
 * StatsCard Component - Premium 2026 Edition
 */
const StatsCard = ({
    icon: Icon,
    title,
    value,
    subtitle,
    color = '#667eea',
    trend,
    isLoading = false,
    onClick,
    variant = 'default',
    glowEffect = true
}) => {
    const isCompact = variant === 'compact';
    const isLarge = variant === 'large';

    const getTrendConfig = (trendValue) => {
        if (!trendValue) return null;
        const isPositive = trendValue.toString().startsWith('+') || parseFloat(trendValue) > 0;
        return {
            isPositive,
            bg: isPositive ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            text: isPositive ? '#10b981' : '#ef4444',
            icon: isPositive ? TrendUpIcon : TrendDownIcon
        };
    };

    const trendConfig = getTrendConfig(trend);

    // Premium gradient based on color
    const getGradient = (baseColor) => {
        const gradients = {
            '#667eea': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '#1976d2': 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
            '#10b981': 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            '#f59e0b': 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            '#ef4444': 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            '#8b5cf6': 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            '#06b6d4': 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
            '#ec4899': 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
            '#4CAF50': 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
            '#ff9800': 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            '#9c27b0': 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
        };
        return gradients[baseColor] || `linear-gradient(135deg, ${baseColor} 0%, ${baseColor}dd 100%)`;
    };

    return (
        <Motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1],
                type: "spring",
                stiffness: 100
            }}
            whileHover={onClick ? {
                scale: 1.03,
                y: -5
            } : { y: -3 }}
            whileTap={onClick ? { scale: 0.98 } : {}}
            style={{ height: '100%' }}
        >
            <Card
                onClick={onClick}
                sx={{
                    height: '100%',
                    cursor: onClick ? 'pointer' : 'default',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                        boxShadow: glowEffect
                            ? `0 20px 40px rgba(0, 0, 0, 0.1), 0 0 40px ${color}25`
                            : '0 20px 40px rgba(0, 0, 0, 0.1)',
                        border: `1px solid ${color}40`,
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: getGradient(color),
                        opacity: 0,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '-50%',
                        left: '-50%',
                        width: '200%',
                        height: '200%',
                        background: `radial-gradient(circle, ${color}08 0%, transparent 50%)`,
                        opacity: 0,
                        transition: 'opacity 0.4s ease',
                        pointerEvents: 'none'
                    },
                    '&:hover::after': {
                        opacity: 1
                    }
                }}
            >
                <CardContent sx={{ p: isCompact ? 2.5 : 3, position: 'relative', zIndex: 1 }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: isCompact ? 2 : 2.5
                    }}>
                        <Motion.div
                            whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Avatar
                                sx={{
                                    width: isCompact ? 48 : isLarge ? 64 : 56,
                                    height: isCompact ? 48 : isLarge ? 64 : 56,
                                    background: getGradient(color),
                                    boxShadow: `0 8px 24px ${color}40`,
                                    '& svg': {
                                        fontSize: isCompact ? 24 : isLarge ? 32 : 28
                                    }
                                }}
                            >
                                {Icon && <Icon sx={{ color: 'white' }} />}
                            </Avatar>
                        </Motion.div>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {trendConfig && (
                                <Motion.div
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    <Chip
                                        icon={<trendConfig.icon sx={{ fontSize: 14, color: `${trendConfig.text} !important` }} />}
                                        label={trend}
                                        size="small"
                                        sx={{
                                            bgcolor: trendConfig.bg,
                                            color: trendConfig.text,
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            borderRadius: 2,
                                            border: 'none',
                                            '& .MuiChip-icon': {
                                                color: trendConfig.text
                                            }
                                        }}
                                    />
                                </Motion.div>
                            )}
                            {onClick && (
                                <Motion.div whileHover={{ x: 3 }}>
                                    <Tooltip title="View details" arrow>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color,
                                                bgcolor: `${color}10`,
                                                '&:hover': {
                                                    bgcolor: `${color}20`
                                                }
                                            }}
                                        >
                                            <GrowthIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Motion.div>
                            )}
                        </Box>
                    </Box>

                    {isLoading ? (
                        <Box>
                            <Skeleton
                                variant="text"
                                width={100}
                                height={isLarge ? 56 : 48}
                                sx={{ borderRadius: 2 }}
                            />
                            <Skeleton variant="text" width={80} height={24} sx={{ borderRadius: 1 }} />
                        </Box>
                    ) : (
                        <>
                            <Typography
                                variant={isCompact ? 'h4' : isLarge ? 'h2' : 'h3'}
                                sx={{
                                    fontWeight: 800,
                                    background: getGradient(color),
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    mb: 0.5,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.2
                                }}
                            >
                                {value}
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    opacity: 0.8,
                                    mb: subtitle ? 0.5 : 0
                                }}
                            >
                                {title}
                            </Typography>

                            {subtitle && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        mt: 0.5
                                    }}
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </Motion.div>
    );
};

/**
 * StatsCardGrid - Premium grid layout for stats cards
 */
export const StatsCardGrid = ({ children, columns = 4, spacing = 3 }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: `repeat(${Math.min(columns, 3)}, 1fr)`,
                lg: `repeat(${columns}, 1fr)`
            },
            gap: spacing
        }}
    >
        {children}
    </Box>
);

export default React.memo(StatsCard);
